import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { applyBranding, useAuth } from '../lib/auth'
import type { Branding } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { Button, Field, Loading, Panel, toast } from '../components/ui'

/**
 * White-label configuration (FSD 3.2).
 *
 * A Tier 3 client holds only `branding.logo` and sees the logo fields alone;
 * a Tier 2 reseller holds `branding.manage` and gets the domain, palette and
 * mail identity too. The server drops anything the caller is not entitled to
 * set, so hiding fields here is a courtesy, not the control.
 */
export function BrandingPage() {
  const { can, tenant, user } = useAuth()
  const qc = useQueryClient()
  const full = can('branding.manage')

  const [form, setForm] = useState<Partial<Branding> & { smtpPassword?: string; mapApiKey?: string }>({})
  const [preview, setPreview] = useState(false)

  const branding = useQuery({
    queryKey: ['branding', tenant?.id],
    queryFn: () => api<Branding>(`/tenants/${tenant!.id}/branding`),
    enabled: Boolean(tenant?.id),
  })

  useEffect(() => {
    if (branding.data) setForm(branding.data)
  }, [branding.data])

  const save = useMutation({
    mutationFn: () =>
      api(`/tenants/${tenant!.id}/branding`, {
        method: 'PUT',
        body: {
          hostname: form.hostname ?? null,
          productName: form.productName ?? null,
          logoUrl: form.logoUrl ?? null,
          logoDarkUrl: form.logoDarkUrl ?? null,
          faviconUrl: form.faviconUrl ?? null,
          loginBgUrl: form.loginBgUrl ?? null,
          primaryColor: form.primaryColor ?? null,
          accentColor: form.accentColor ?? null,
          defaultTheme: form.defaultTheme ?? null,
          supportEmail: form.supportEmail ?? null,
          supportPhone: form.supportPhone ?? null,
          mapProvider: form.mapProvider ?? null,
          smtpHost: form.smtpHost ?? null,
          smtpPort: form.smtpPort ?? null,
          smtpUsername: form.smtpUsername ?? null,
          smtpPassword: form.smtpPassword || null,
          mailFromName: form.mailFromName ?? null,
          mailFromEmail: form.mailFromEmail ?? null,
          mapApiKey: form.mapApiKey || null,
        },
      }),
    onSuccess: () => {
      toast('Branding saved', 'ok')
      void qc.invalidateQueries({ queryKey: ['branding'] })
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  /** Applies the palette live so the operator sees the result before saving. */
  function applyPreview() {
    applyBranding({
      ...(branding.data as Branding),
      accentColor: form.accentColor ?? '#0098a8',
      primaryColor: form.primaryColor ?? '#4f46e5',
      defaultTheme: form.defaultTheme ?? 'dark',
      mapProvider: form.mapProvider ?? 'osm',
    })
    setPreview(true)
    toast('Preview applied — save to make it permanent', 'info')
  }

  if (branding.isLoading) return <Loading label="Loading branding" />

  return (
    <Page
      title="Branding"
      subtitle={
        full
          ? 'How your customers see the platform: domain, colours, logo and outgoing mail'
          : 'Your logo, shown throughout the platform'
      }
      actions={
        <>
          {full && (
            <Button onClick={applyPreview}>
              <Icon name="palette" size={13} />
              Preview
            </Button>
          )}
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="grid lg:grid-cols-[1fr_320px] gap-4 items-start max-w-5xl">
        <div className="space-y-4">
          {/* --- identity --- */}
          <Panel title="Identity">
            <div className="space-y-3.5">
              {full && (
                <Field
                  label="Product name"
                  hint="Replaces “SmartVision IoT” everywhere your customers look"
                >
                  <input className="field" value={form.productName ?? ''} onChange={set('productName')} placeholder="Acme Track" />
                </Field>
              )}

              <div className="grid sm:grid-cols-2 gap-3.5">
                <Field label="Logo URL" hint="Shown on the sign-in page and in the sidebar">
                  <input className="field" value={form.logoUrl ?? ''} onChange={set('logoUrl')} placeholder="https://…/logo.svg" />
                </Field>
                <Field label="Favicon URL">
                  <input className="field" value={form.faviconUrl ?? ''} onChange={set('faviconUrl')} placeholder="https://…/favicon.png" />
                </Field>
              </div>

              {form.logoUrl && (
                <div
                  className="flex items-center gap-4 px-3.5 py-3 rounded-[3px]"
                  style={{ background: 'var(--color-hull)' }}
                >
                  <span className="label">Preview</span>
                  <img
                    src={form.logoUrl}
                    alt="Logo preview"
                    className="h-7 max-w-[180px] object-contain"
                    onError={(e) => ((e.target as HTMLImageElement).style.opacity = '0.25')}
                  />
                </div>
              )}

              {full && (
                <Field label="Login background URL" hint="Large image behind the sign-in form">
                  <input className="field" value={form.loginBgUrl ?? ''} onChange={set('loginBgUrl')} />
                </Field>
              )}
            </div>
          </Panel>

          {full && (
            <>
              {/* --- domain --- */}
              <Panel title="Custom domain">
                <div className="space-y-3.5">
                  <Field
                    label="Hostname"
                    hint="Point a CNAME at the platform, then enter it here. The sign-in page for this hostname uses your branding."
                  >
                    <input
                      className="field"
                      value={form.hostname ?? ''}
                      onChange={set('hostname')}
                      placeholder="tracking.yourcompany.com"
                    />
                  </Field>

                  {form.hostname && (
                    <div
                      className="px-3 py-2.5 rounded-[3px] text-[12px] leading-relaxed font-[var(--font-mono)]"
                      style={{ background: 'var(--color-hull)', color: 'var(--color-ink-2)' }}
                    >
                      {form.hostname}. CNAME {window.location.hostname}.
                    </div>
                  )}
                </div>
              </Panel>

              {/* --- palette --- */}
              <Panel title="Palette">
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <ColorField
                      label="Accent"
                      hint="Buttons, highlights, the live-feed indicator"
                      value={form.accentColor ?? '#0098a8'}
                      onChange={(v) => setForm((f) => ({ ...f, accentColor: v }))}
                    />
                    <ColorField
                      label="Primary"
                      hint="Secondary highlights"
                      value={form.primaryColor ?? '#4f46e5'}
                      onChange={(v) => setForm((f) => ({ ...f, primaryColor: v }))}
                    />
                  </div>

                  <Field label="Default theme" hint="What new users see before they pick their own">
                    <select
                      className="field"
                      value={form.defaultTheme ?? 'dark'}
                      onChange={(e) => setForm((f) => ({ ...f, defaultTheme: e.target.value as 'dark' | 'light' }))}
                    >
                      <option value="dark">Dark</option>
                      <option value="light">Light</option>
                    </select>
                  </Field>

                  <div
                    className="px-3 py-2.5 rounded-[3px] text-[12px] leading-relaxed"
                    style={{
                      background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--accent) 22%, transparent)',
                    }}
                  >
                    Vehicle status colours — moving, idle, stopped, no signal — are fixed and cannot
                    be rebranded. A dispatcher moving between accounts must never have to relearn
                    what red means.
                  </div>
                </div>
              </Panel>

              {/* --- map --- */}
              <Panel title="Map provider">
                <div className="grid sm:grid-cols-2 gap-3.5">
                  <Field label="Provider">
                    <select
                      className="field"
                      value={form.mapProvider ?? 'osm'}
                      onChange={(e) => setForm((f) => ({ ...f, mapProvider: e.target.value }))}
                    >
                      <option value="osm">OpenStreetMap (no key required)</option>
                      <option value="maptiler">MapTiler</option>
                      <option value="mapbox">Mapbox</option>
                    </select>
                  </Field>
                  {form.mapProvider && form.mapProvider !== 'osm' && (
                    <Field label="API key" hint="Stored encrypted; never returned to the browser in full">
                      <input
                        className="field"
                        type="password"
                        value={form.mapApiKey ?? ''}
                        onChange={set('mapApiKey')}
                        placeholder="Leave blank to keep the current key"
                      />
                    </Field>
                  )}
                </div>
              </Panel>

              {/* --- mail --- */}
              <Panel
                title="Outgoing mail"
                subtitle="Alerts and reports are sent from your own server, under your own name"
              >
                <div className="space-y-3.5">
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <Field label="From name">
                      <input className="field" value={form.mailFromName ?? ''} onChange={set('mailFromName')} placeholder="Acme Track Alerts" />
                    </Field>
                    <Field label="From address">
                      <input className="field" type="email" value={form.mailFromEmail ?? ''} onChange={set('mailFromEmail')} placeholder="alerts@yourcompany.com" />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-[1fr_100px] gap-3.5">
                    <Field label="SMTP host">
                      <input className="field" value={form.smtpHost ?? ''} onChange={set('smtpHost')} placeholder="smtp.yourcompany.com" />
                    </Field>
                    <Field label="Port">
                      <input
                        className="field tnum"
                        type="number"
                        value={form.smtpPort ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, smtpPort: Number(e.target.value) || undefined }))}
                        placeholder="587"
                      />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <Field label="Username">
                      <input className="field" value={form.smtpUsername ?? ''} onChange={set('smtpUsername')} />
                    </Field>
                    <Field label="Password" hint="Encrypted at rest; blank keeps the current one">
                      <input className="field" type="password" value={form.smtpPassword ?? ''} onChange={set('smtpPassword')} />
                    </Field>
                  </div>
                </div>
              </Panel>

              {/* --- support --- */}
              <Panel title="Support contact" subtitle="Shown on the sign-in page">
                <div className="grid sm:grid-cols-2 gap-3.5">
                  <Field label="Support email">
                    <input className="field" type="email" value={form.supportEmail ?? ''} onChange={set('supportEmail')} />
                  </Field>
                  <Field label="Support phone">
                    <input className="field tnum" value={form.supportPhone ?? ''} onChange={set('supportPhone')} />
                  </Field>
                </div>
              </Panel>
            </>
          )}
        </div>

        {/* --- live preview --- */}
        <aside className="space-y-4 lg:sticky lg:top-0">
          <Panel title="Sign-in preview" padded={false}>
            <div
              className="p-5 flex flex-col gap-4"
              style={{ background: 'var(--color-hull)', minHeight: 220 }}
            >
              <div className="flex items-center gap-2.5">
                {form.logoUrl ? (
                  <img src={form.logoUrl} alt="" className="h-6 max-w-[140px] object-contain" />
                ) : (
                  <span
                    className="w-5 h-5 rounded-full border-2"
                    style={{ borderColor: form.accentColor ?? 'var(--accent)' }}
                  />
                )}
                <span className="font-[var(--font-label)] font-bold uppercase tracking-[0.12em] text-[13px]">
                  {form.productName || 'SmartVision IoT'}
                </span>
              </div>

              <div className="space-y-2">
                <div className="h-8 rounded-[3px]" style={{ background: 'var(--color-panel)' }} />
                <div className="h-8 rounded-[3px]" style={{ background: 'var(--color-panel)' }} />
                <div
                  className="h-8 rounded-[3px] grid place-items-center text-[12px] font-semibold"
                  style={{
                    background: form.accentColor ?? 'var(--accent)',
                    color: readable(form.accentColor ?? '#0098a8'),
                  }}
                >
                  Sign in
                </div>
              </div>

              {form.supportEmail && (
                <div className="text-[11px] text-[var(--color-ink-4)] pt-2 rule-t">{form.supportEmail}</div>
              )}
            </div>
          </Panel>

          {preview && (
            <div
              className="px-3 py-2.5 rounded-[3px] text-[12px] leading-relaxed"
              style={{
                color: 'var(--text-warn)',
                background: 'color-mix(in srgb, var(--color-warn) 9%, transparent)',
                border: '1px solid color-mix(in srgb, var(--color-warn) 26%, transparent)',
              }}
            >
              Preview is applied to your own session only. Reload to discard it, or save to apply it
              for everyone in {tenant?.name}.
            </div>
          )}

          {!full && (
            <p className="text-[12px] text-[var(--color-ink-3)] leading-relaxed px-1">
              Your plan allows a custom logo. Domain, colours and outgoing mail are configured by{' '}
              {user?.tenantKind === 'client' ? 'your provider' : 'the platform owner'}.
            </p>
          )}
        </aside>
      </div>
    </Page>
  )
}

function ColorField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string
  hint?: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <Field label={label} hint={hint}>
      <div className="flex gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-[34px] rounded-[3px] border border-[var(--color-rule)] bg-transparent cursor-pointer"
          aria-label={`${label} colour picker`}
        />
        <input className="field tnum grow" value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </Field>
  )
}

function readable(hex: string): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim())
  if (!m) return '#06080b'
  const n = parseInt(m[1]!, 16)
  const luma = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255
  return luma > 0.6 ? '#06080b' : '#ffffff'
}
